// Floating "Aria" concierge — a chat bubble (bottom-right) that expands into a live, streaming chat
// for logged-out marketing visitors. Anonymous → /v1/public/concierge (sales persona,
// guardrailed, refuses medical). Seeds a greeting + suggestion chips and always offers a Create-account
// CTA so a good thread converts. Other surfaces (e.g. the home AI section's CTA) open it by
// dispatching a window "aria:open" event.
import { useEffect, useRef, useState } from "react";
import { Markdown } from "@championcare/ui";
import { conciergeStream, type ConciergeMessage } from "../lib/conciergeStream";
import { patientSignupUrl } from "../config";

const GREETING =
  "Hey! 👋 I'm Aria. Ask me anything about Champion Care — the plans, how visits work, or how to get started.";
const SUGGESTIONS = ["What's included?", "How much is it?", "How do visits work?"];

const ChatIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z" />
  </svg>
);
const SendIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const CloseIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function AriaWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ConciergeMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [live, setLive] = useState(""); // the reply currently streaming in
  const listRef = useRef<HTMLDivElement>(null);

  // Let other parts of the site open the widget (e.g. the home "Try AI Concierge" CTA).
  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener("aria:open", openIt);
    return () => window.removeEventListener("aria:open", openIt);
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, live, open]);

  async function send(text: string) {
    const content = text.trim();
    if (!content || streaming) return;
    const history: ConciergeMessage[] = [...messages, { role: "user", content }];
    setMessages(history);
    setDraft("");
    setStreaming(true);
    setLive("");

    let acc = "";
    try {
      for await (const ev of conciergeStream(history)) {
        if (ev.type === "chunk") {
          acc += ev.text;
          setLive(acc);
        } else {
          // refusal / error — discard any partial, show this text
          acc = ev.text;
          setLive(acc);
        }
      }
    } catch {
      acc = "Sorry — something went wrong. Please try again.";
    }

    setMessages([...history, { role: "assistant", content: acc }]);
    setLive("");
    setStreaming(false);
  }

  if (!open) {
    return (
      <button className="aria-fab" onClick={() => setOpen(true)} aria-label="Chat with Aria">
        {ChatIcon}
      </button>
    );
  }

  const empty = messages.length === 0 && !streaming;

  return (
    <div className="aria-panel" role="dialog" aria-label="Aria — AI Concierge">
      <div className="aria-panel__head">
        <span className="aria-panel__avatar">A</span>
        <div className="aria-panel__meta">
          <div className="aria-panel__name">Aria · AI Concierge</div>
          <div className="aria-panel__status">Online</div>
        </div>
        <button className="aria-panel__close" onClick={() => setOpen(false)} aria-label="Close chat">
          {CloseIcon}
        </button>
      </div>

      <div className="aria-panel__list" ref={listRef}>
        {empty && (
          <>
            <div className="ai-msg bot"><Markdown text={GREETING} /></div>
            <div className="aria-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} className="aria-chip" onClick={() => send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`ai-msg ${m.role === "user" ? "user" : "bot"}`}>
            {m.role === "user" ? m.content : <Markdown text={m.content} />}
          </div>
        ))}

        {streaming && <div className="ai-msg bot">{live ? <Markdown text={live} /> : "…"}</div>}
      </div>

      <a className="aria-cta" href={patientSignupUrl()}>
        Create your account →
      </a>

      <div className="aria-panel__compose">
        <input
          className="aria-panel__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send(draft)}
          placeholder="Ask Aria…"
          aria-label="Message Aria"
          disabled={streaming}
        />
        <button
          className="aria-panel__send"
          onClick={() => send(draft)}
          disabled={!draft.trim() || streaming}
          aria-label="Send"
        >
          {SendIcon}
        </button>
      </div>
    </div>
  );
}
